import { useId } from 'react';

import { css } from '@compiled/react';

import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import { Button } from '@/libraries/design/components/button/src/button';
import { Link } from '@/libraries/design/components/link/src/link';
import { wacToken } from '@/libraries/design/foundations';
import { RichTextComponent } from '@/libraries/design/utils';
import { DataEventProps } from '@/libraries/wpl/foundations/src/data-event-props';

export interface ArticleCtaProps {
	title: string;
	copy?: string | React.ReactElement<typeof RichTextComponent>;
	buttonText: string;
	buttonHref: string;
	linkText?: string;
	linkHref?: string;
	dataEventProps?: DataEventProps;
}

const containerStyle = css({
	backgroundColor: wacToken('color.extended.blue100'),
	borderRadius: wacToken('corner.radius.card'),
	padding: token('space.400'),
	display: 'flex',
	gap: token('space.400'),
	width: 'fit-content',

	[media.below.md]: {
		padding: token('space.300'),
	},

	[media.below.sm]: {
		flexDirection: 'column',
	},
});

const copyContainerStyle = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.050'),
	justifyContent: 'center',
});

const ctaContainerStyle = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.200'),
	justifyContent: 'center',
	alignItems: 'center',

	[media.below.sm]: {
		alignItems: 'flex-start',
	},
});

const noWrapLinkStyle = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	a: {
		whiteSpace: 'nowrap',
	},
});

const marginZeroCss = css({
	marginBlock: token('space.0'),
});

export const ArticleCta = (props: ArticleCtaProps) => {
	const uniqueId = useId();
	const titleId = `article-cta-title-${uniqueId}`;
	const buttonId = `article-cta-button-${uniqueId}`;
	const linkId = `article-cta-link-${uniqueId}`;

	const copyElement: React.ReactNode =
		typeof props.copy === 'string' ? <p css={marginZeroCss}>{props.copy}</p> : props.copy;
	return (
		<div css={containerStyle}>
			<div css={copyContainerStyle}>
				<p id={titleId} className="card-title" css={marginZeroCss}>
					{props.title}
				</p>
				{props.copy && copyElement}
			</div>
			<div css={[ctaContainerStyle, noWrapLinkStyle]}>
				<Button
					id={buttonId}
					variant="primary"
					size="small"
					color="blue"
					href={props.buttonHref}
					ariaLabelledby={`${buttonId} ${titleId}`}
					dataEventProps={props.dataEventProps}
				>
					{props.buttonText}
				</Button>
				{props.linkText && props.linkHref && (
					<Link
						id={linkId}
						text={props.linkText}
						href={props.linkHref}
						ariaLabelledby={`${linkId} ${titleId}`}
						aria-label={props.linkText}
						dataEventProps={props.dataEventProps}
					/>
				)}
			</div>
		</div>
	);
};
